import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost, apiDelete, apiPatch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { workspacePath } from "../lib/workspaces";
import { UserPlus, Trash2, Loader2 } from "lucide-react";

interface TeamMember {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  role: string;
  createdAt: string;
}

export function Team() {
  const { userId, role } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState<"ADMIN" | "VIEWER">("VIEWER");
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function loadMembers() {
    const res = await apiGet<TeamMember[]>("/team");
    if (res.success && res.data) {
      setMembers(res.data);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadMembers();
  }, []);

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    setInviting(true);
    setError(null);
    setErrorKey(null);
    setSuccess(null);

    const res = await apiPost<TeamMember>("/team", {
      email: inviteEmail,
      name: inviteName || undefined,
      role: inviteRole,
    });

    if (res.success && res.data) {
      setMembers((prev) => [...prev, res.data!]);
      setInviteEmail("");
      setInviteName("");
      setShowInvite(false);
      setSuccess(`Invitation sent to ${res.data.email}`);
    } else {
      setError(res.error?.message || "Failed to invite member");
      setErrorKey(res.error?.key ?? null);
    }
    setInviting(false);
  }

  async function handleRoleChange(member: TeamMember, role: "ADMIN" | "VIEWER") {
    setError(null);
    setErrorKey(null);
    setSuccess(null);
    const res = await apiPatch<TeamMember>(`/team/${member.id}`, { role });
    if (res.success && res.data) {
      setMembers((previous) => previous.map((item) => item.id === member.id ? res.data! : item));
      setSuccess(`${member.email} is now ${role === "ADMIN" ? "an admin" : "a viewer"}.`);
    } else {
      setError(res.error?.key === "LAST_ADMIN" ? "Every workspace must have at least one admin." : res.error?.message || "Failed to update role");
      setErrorKey(res.error?.key ?? null);
    }
  }

  async function handleRemove(member: TeamMember) {
    if (!confirm(`Remove ${member.email} from the team?`)) return;
    setError(null);
    setSuccess(null);

    const res = await apiDelete(`/team/${member.id}`);
    if (res.success) {
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      setSuccess(`${member.email} has been removed.`);
    } else {
      setError(res.error?.message || "Failed to remove member");
    }
  }

  if (loading) return <div className="p-6 text-stone-500">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 font-display">Team</h1>
        {role === "ADMIN" && <button
          onClick={() => {
            setShowInvite(!showInvite);
            setError(null);
          }}
          className="flex items-center gap-2 px-3 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
        >
          <UserPlus size={16} />
          Invite Member
        </button>}
      </div>

      {showInvite && (
        <div className="bg-white rounded-xl border border-stone-200 p-5 mb-4">
          <h2 className="text-sm font-semibold text-stone-900 mb-3">Invite a team member</h2>
          <form onSubmit={handleInvite} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                placeholder="colleague@company.com"
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Role</label>
              <select value={inviteRole} onChange={(event) => setInviteRole(event.target.value as "ADMIN" | "VIEWER")} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent">
                <option value="VIEWER">Viewer</option>
                <option value="ADMIN">Admin</option>
              </select>
              <p className="mt-1 text-xs text-stone-400">Existing SingVMS users can be added to this workspace.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Name (optional)</label>
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Their name"
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="submit"
                disabled={inviting}
                className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {inviting && <Loader2 size={14} className="animate-spin" />}
                {inviting ? "Sending..." : "Send Invite"}
              </button>
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-900"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-4">
          {error}
          {errorKey === "PLAN_LIMIT" && (
            <>
              {" "}
              <Link to={workspacePath("billing")} className="underline font-medium hover:text-red-700">
                Upgrade your plan
              </Link>
            </>
          )}
        </div>
      )}
      {success && <p className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg mb-4">{success}</p>}

      <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="text-sm font-medium text-stone-900">
                {m.name || m.email}
                {m.userId === userId && (
                  <span className="ml-2 text-xs text-stone-400">(you)</span>
                )}
              </p>
              {m.name && (
                <p className="text-xs text-stone-500">{m.email}</p>
              )}
              <p className="text-xs text-stone-400 mt-0.5">
                {m.role} &middot; Joined {new Date(m.createdAt).toLocaleDateString()}
              </p>
            </div>
            {role === "ADMIN" && m.userId !== userId && <div className="flex items-center gap-2">
              <select value={m.role} onChange={(event) => void handleRoleChange(m, event.target.value as "ADMIN" | "VIEWER")} className="rounded-lg border border-stone-200 bg-white px-2 py-1.5 text-xs text-stone-600">
                <option value="VIEWER">Viewer</option><option value="ADMIN">Admin</option>
              </select>
              <button onClick={() => handleRemove(m)} className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Remove member"><Trash2 size={16} /></button>
            </div>}
          </div>
        ))}
        {members.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-stone-400">
            No team members yet. Invite someone to get started.
          </div>
        )}
      </div>
    </div>
  );
}
